"use client";

import * as React from "react";
import { MobileNavigationProvider } from "./MobileNavigationContext";
import { BottomNavBar } from "./BottomNavBar";
import { FullScreenMenu } from "./FullScreenMenu";
import { MobileSearchOverlay } from "./MobileSearchOverlay";
import { SwipeNavigation } from "./SwipeNavigation";
import { NavigationTransitions } from "./NavigationTransitions";
import { useIsMobile } from "@/hooks/use-mobile";

interface MobileNavigationProps {
  children: React.ReactNode;
  enableSwipeBack?: boolean;
  enableTransitions?: boolean;
  transitionMode?: "fade" | "slide" | "slide-up" | "scale" | "none";
}

/**
 * MobileNavigation - Complete mobile navigation solution
 *
 * Features:
 * - Bottom navigation bar (thumb-zone optimized)
 * - Full-screen hamburger menu overlay
 * - Swipe-to-go-back gesture support
 * - Voice search integration
 * - Smooth page transitions
 *
 * Usage:
 * ```tsx
 * <MobileNavigation>
 *   <YourAppContent />
 * </MobileNavigation>
 * ```
 */
export function MobileNavigation({
  children,
  enableSwipeBack = true,
  enableTransitions = true,
  transitionMode = "slide-up",
}: MobileNavigationProps) {
  const isMobile = useIsMobile();

  // For desktop, just render children directly
  if (!isMobile) {
    return <>{children}</>;
  }

  return (
    <MobileNavigationProvider>
      {/* Swipe Navigation Wrapper */}
      <SwipeNavigation enabled={enableSwipeBack}>
        {/* Page Transitions */}
        {enableTransitions ? (
          <NavigationTransitions mode={transitionMode}>
            {children}
          </NavigationTransitions>
        ) : (
          children
        )}
      </SwipeNavigation>

      {/* Navigation Overlays */}
      <FullScreenMenu />
      <MobileSearchOverlay />

      {/* Bottom Navigation Bar */}
      <BottomNavBar />
    </MobileNavigationProvider>
  );
}

export default MobileNavigation;
